<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">

          <v-card class="pa-4">
            <div class="d-flex justify-space-between mb-4">
              <h2 class="text-h6 font-weight-bold">Budget Sub Head List</h2>

              <v-btn
                color="primary"
                variant="tonal"
                prepend-icon="mdi mdi-plus"
                rounded="xl"
                @click="openCreate"
              >
                New Sub Head
              </v-btn>
            </div>

            <v-data-table
              :headers="headers"
              :items="budgetSubHeadList"
              class="elevation-1"
            >

              <template #item.index="{ index }">
                {{ ++index }}
              </template>

              <template #item.budgetHead="{ item }">
                {{ item.budget_head?.name }}
              </template>

              <template #item.budgetType="{ item }">
                {{ item.budget_head?.budget_type?.name }}
              </template>

              <template #item.status="{ item }">
                <v-chip v-if="item.status == 1" color="success" size="small">
                  Active
                </v-chip>
                <v-chip v-else color="red" size="small">
                  Inactive
                </v-chip>
              </template>

              <template #item.actions="{ item }">
                <v-icon
                  class="me-2"
                  color="primary"
                  @click="editBudgetSubHead(item)"
                >
                  mdi-pencil-outline
                </v-icon>
              </template>

            </v-data-table>
          </v-card>

        </v-col>
      </v-row>

      <!-- MODAL -->
      <v-dialog v-model="dialog" max-width="600">
        <v-card class="pa-3">

          <v-card-title>
            {{ isEdit ? "Update Sub Head" : "Create Sub Head" }}
          </v-card-title>

          <v-card-text>
            <v-row>

        

              <v-col cols="12">
                <v-select
                  v-model="budget_type_id"
                  :items="budgetTypeList"
                  item-title="name"
                  item-value="id"
                  label="Budget Type *"
                  variant="outlined"
                  density="compact"
                  @update:modelValue="filterHeads"
                  hide-details
                />
              </v-col>

              <v-col cols="12">
                <v-select
                  v-model="budget_head_id"
                  :items="filteredHeadList"
                  item-title="name"
                  item-value="id"
                  label="Budget Head *"
                  variant="outlined"
                  density="compact"
                  :error-messages="budgetHeadError"
                  hide-details
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="name"
                  label="Name *"
                  variant="outlined"
                  density="compact"
                  :error-messages="nameError"
                  hide-details
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="max_limit"
                  label="Max Limit"
                  type="number"
                  variant="outlined"
                  density="compact"
                  :error-messages="maxLimitError"
                  hide-details
                />
              </v-col>

              <v-col cols="12">
                <v-checkbox
                  label="Active"
                  v-model="status"
                  :true-value="1"
                  :false-value="0"
                />
              </v-col>

            </v-row>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-spacer />
                <v-btn class="bg-red" @click="dialog=false"> Close </v-btn>
                <v-btn class="bg-brand w-15 ms-2" @click="saveBudgetSubHead"> {{ isEdit ? "Update" : "Create" }} </v-btn>
          </v-card-actions>

        </v-card>
      </v-dialog>

    </v-container>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import { useStore } from "vuex"

import { useForm, useField } from "vee-validate"
import * as yup from "yup"

const store = useStore()

/* -----------------------------
STATE
--------------------------------*/
const dialog = ref(false)
const isEdit = ref(false)
const subHeadId = ref(null)

const filteredHeadList = ref([])

/* -----------------------------
VALIDATION
--------------------------------*/
const schema = yup.object({
  name: yup.string().required("Name is required"),
  budget_head_id: yup
    .number()
    .required("Budget head is required")
    .typeError("Budget head is required"),
  max_limit: yup.number().nullable(),
  status: yup.number()
})

const { handleSubmit, setErrors, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    name: "",
    budget_head_id: null,
    max_limit: null,
    status: 1
  }
})

const { value: name, errorMessage: nameError } = useField("name")
const { value: budget_head_id, errorMessage: budgetHeadError } = useField("budget_head_id")
const { value: max_limit, errorMessage: maxLimitError } = useField("max_limit")
const { value: status } = useField("status")

const budget_type_id = ref(null)

/* -----------------------------
TABLE
--------------------------------*/
const headers = [
  { title: "#SL", key: "index" },
  { title: "Code", key: "code" },
  { title: "Name", key: "name" },
  { title: "Type", key: "budgetType" },
  { title: "Head", key: "budgetHead" },
  { title: "Max Limit", key: "max_limit" },
  { title: "Status", key: "status" },
  { title: "Actions", key: "actions" }
]

const budgetSubHeadList = computed(() => store.getters.getBudgetSubHeadList)
const budgetHeadList = computed(() => store.getters.getBudgetHeadList)
const budgetTypeList = computed(() => store.getters.getBudgetTypeList)

/* -----------------------------
FILTER HEAD BY TYPE
--------------------------------*/
const filterHeads = () => {
  filteredHeadList.value = budgetHeadList.value.filter(
    (item) => item.budget_type_id === budget_type_id.value
  )

  budget_head_id.value = null
}

/* -----------------------------
LOAD DATA
--------------------------------*/
const loadData = async () => {
  store.commit("SET_LOADING", { color: "primary" })

  await store.dispatch("getBudgetTypeList")
  await store.dispatch("getBudgetHeadList")
  await store.dispatch("getBudgetSubHeadList")

  store.commit("UNSET_LOADING")
}

/* -----------------------------
CREATE
--------------------------------*/
const openCreate = () => {
  isEdit.value = false

  resetForm({
    values: {
      name: "",
      budget_head_id: null,
      max_limit: null,
      status: 1
    }
  })

  budget_type_id.value = null
  filteredHeadList.value = []

  dialog.value = true
}

/* -----------------------------
EDIT
--------------------------------*/
const editBudgetSubHead = async (item) => {
  isEdit.value = true
  subHeadId.value = item.id

  const response = await store.dispatch("getBudgetSubHeadById", item.id)
  const data = response.data

  name.value = data.name
  status.value = data.status
  max_limit.value = data.max_limit

  budget_type_id.value = data.budget_head.budget_type_id

  filterHeads()

  budget_head_id.value = data.budget_head_id

  dialog.value = true
}

/* -----------------------------
PAYLOAD
--------------------------------*/
const buildPayload = () => {
  return {
    name: name.value,
    budget_head_id: budget_head_id.value,
    max_limit: max_limit.value,
    status: status.value
  }
}

/* -----------------------------
SAVE
--------------------------------*/
const saveBudgetSubHead = handleSubmit(async () => {
  try {
    const payload = buildPayload()

    if (isEdit.value) {
      payload.id = subHeadId.value
      await store.dispatch("updateBudgetSubHead", payload)
    } else {
      await store.dispatch("saveBudgetSubHead", payload)
    }

    dialog.value = false
    loadData()

  } catch (error) {
    if (error.response && error.response.status === 422) {
      setErrors(error.response.data.errors)
    }
  }
})

/* -----------------------------
MOUNT
--------------------------------*/
onMounted(() => {
  loadData()
})
</script>